import { Fragment, useCallback, useState } from "react";
import { MAP_DISPLAY_MODES, MAP_LAYER_TYPES } from "../constants";
import { WMSTileLayer, useMap } from "react-leaflet";

import { FeaturesLayer } from "./FeaturesLayer";
import { RasterInformation } from "./RasterInformation/RasterInformation";
import { ScoutingLayer } from "./Scouting/ScoutingLayer";
import { selectShowPlotLabels } from "../selectors/resultMap";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";
import { useSelector } from "react-redux";

export const TrialDisplay = () => {
  const map = useMap();
  const resultMap = useSelector(({ resultMap }) => resultMap);
  const fitZoomToSite = useCallback(() => {
    if (resultMap.trial?.bounding_box) {
      //zoom on site bouding box if no result
      map?.fitBounds(resultMap.trial.bounding_box);
      // If no bounding box and features are available, create a bounding box from features geometry
    } else if (resultMap.features.length > 0) {
      const corners = resultMap.features.reduce(
        (acc, { geometry }) => {
          geometry.coordinates[0].forEach(([x, y]) => {
            acc[0] = Math.min(x, acc[0]);
            acc[1] = Math.min(y, acc[1]);
            acc[2] = Math.max(x, acc[2]);
            acc[3] = Math.max(y, acc[3]);
          });
          return acc;
        },

        [Infinity, Infinity, -Infinity, -Infinity]
      );
      // Create bounding box based on deduced coordinates
      map?.fitBounds([
        [corners[1], corners[0]],
        [corners[3], corners[2]],
      ]);
    } else if (resultMap.trial?.trial_position) {
      // zoom approx. on site if there is no bounding box
      map?.setView(resultMap.trial.trial_position, 15);
    }
  }, [
    map,
    resultMap.features,
    resultMap.trial.bounding_box,
    resultMap.trial.trial_position,
  ]);

  // Fit zoom to site on trial change only and on remount
  const [prevTrial, setPrevTrial] = useState(null);
  if (map && resultMap.trial !== prevTrial) {
    setPrevTrial(resultMap.trial);
    fitZoomToSite();
  }

  useKeyboardShortcut("c", fitZoomToSite);

  const mapDisplayMode = useSelector((state) => state.resultMap.mapDisplayMode);
  const isFieldMapMode = mapDisplayMode === MAP_DISPLAY_MODES.FIELD_MAP;
  const showPlotLabels = useSelector(selectShowPlotLabels);

  return (
    resultMap.mapLayers
      // Hidden plots stay mounted while their labels show (white outlines).
      .filter(
        ({ visible, type }) =>
          visible || (type === MAP_LAYER_TYPES.GEOJSON && showPlotLabels)
      )
      .map((layer) => (
        <Fragment key={layer.name ?? layer.type.name}>
          {layer.type === MAP_LAYER_TYPES.SCOUTING && <ScoutingLayer />}
          {layer.type === MAP_LAYER_TYPES.GEOJSON && !isFieldMapMode && (
            <FeaturesLayer />
          )}
          {layer.type === MAP_LAYER_TYPES.ORTHO && (
            <WMSTileLayer
              layers={
                resultMap.trial.ortho_layer[resultMap.trial_date][layer.name]
              }
              key={`${resultMap.trial.id}${resultMap.trial_date}${layer.name}`}
              url={process.env.REACT_APP_GEOSERVER_URL}
              format="image/png"
              transparent="true"
              opacity={layer.opacity}
              tiled={true}
              zIndex={1100 - layer.priority}
              maxZoom={50}
            />
          )}
          {layer.visible && <RasterInformation />}
        </Fragment>
      ))
  );
};
